import React, { HTMLAttributes, useEffect, useRef, useState } from 'react';
import Header from '#/components/Form/Header';
import useProtectedSpotify from '#/hooks/useProtectedAxios';
import {
  createActionArtistAddDetails,
  createActionArtistErrorDetails,
  createActionArtistStartLoading,
  createActionArtistSetAlbumType,
  createActionArtistSetAlbums,
  createActionArtistStopLoading,
  createActionArtistSetIsFollowed,
  createActionArtistReset,
  createActionArtistSetTrackIsFavorite,
} from '#/redux/actions/artist';
import { TRootState } from '#/redux/store';
import ApiManager from '#/services/api/ApiManager';
import {
  IArtistAlbums,
  IArtistResponse,
  IArtistTopTracksResponse,
} from '#/types/API/artists';
import {
  albumTypes,
  header2ClassName,
  spotifyApiConfig,
} from '#/utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';

import tinyColor from 'tinycolor2';
import Vibrant from 'node-vibrant';
import PlayButton from '#/components/Form/PlayButton/PlayButton';
import TransparentButton from '#/components/Form/TransparentButton/TransparentButton';
import TrackListItem from '#/components/Layout/TrackListItem/TrackListItem';
import PreviewLayout from '#/components/Layout/PreviewLayout/PreviewLayout';
import { getItemsPerBreakpoint } from '#/utils/getItemsPerBreakpoint';
import useViewport from '#/hooks/useViewport';
import Badge from '#/components/Form/Badge/Badge';
import { capitalize } from 'lodash';

import ArtistPreview from '#/components/Layout/ArtistPreview/ArtistPreview';
import { ROUTE_NAMES } from '#/router/routes';
import LoadingPage from '#/components/Layout/LoadingPage/LoadingPage';
import TopItemPreview from '#/components/Layout/TopItemPreview/TopItemPreview';
import useDeviceId from '#/hooks/useDeviceId';
import {
  emptyContainsResponse,
  emptyRelatedArtistsResponse,
  emptyTopTracksResponse,
  okResponse,
  urisParameter,
} from '#/utils/spotifyApiHelpers';

const artistSelector = (state: TRootState) => state.artist;

type TColor = [number, number, number];

const Artist = () => {
  const imageReference = useRef<HTMLImageElement>(null);

  const { viewportWidth } = useViewport();

  const navigate = useNavigate();

  const [expandTopTracks, setExpandTopTracks] = useState(false);

  const [dominantColor, setDominantColor] = useState<undefined | TColor>();

  const artist = useSelector(artistSelector);

  const {
    details,
    albums,
    selectedAlbumType,
    topTracks,
    relatedArtists,
    apearsOn,
    isFollowed,
    _error,
    _loading,
  } = artist;

  const dispatch = useDispatch();

  const protectedSpotify = useProtectedSpotify();

  const { id } = useParams();

  const albumsLimit = 6;

  const { retrieveDeviceId } = useDeviceId();

  useEffect(() => {
    const getArtistDetails = async () => {
      dispatch(createActionArtistStartLoading());
      if (id) {
        const apiMannager = new ApiManager(spotifyApiConfig, protectedSpotify);

        const responseDetails = await apiMannager.getEntity<IArtistResponse>(
          'artists',
          id
        );

        if ('error' in responseDetails) {
          // TODO: handle error
          dispatch(createActionArtistErrorDetails());
        } else {
          const details = responseDetails.data;
          const responseTracks = await okResponse<IArtistTopTracksResponse>(
            emptyTopTracksResponse
          );
          const responseAlbums = await apiMannager.getEntity<IArtistAlbums>(
            'artists',
            `${id}/albums`,
            { limit: albumsLimit }
          );
          const responseApearsOn = await apiMannager.getEntity<IArtistAlbums>(
            'artists',
            `${id}/albums`,
            { limit: albumsLimit, include_groups: 'appears_on' }
          );
          const responseRelatedArtists = await okResponse(
            emptyRelatedArtistsResponse
          );
          const responseIsFollowed = await apiMannager.getAllEntities<
            boolean[]
          >('libraryContains', {
            uris: urisParameter('artist', id),
          });
          if (
            'error' in responseTracks ||
            'error' in responseAlbums ||
            'error' in responseApearsOn ||
            'error' in responseRelatedArtists ||
            'error' in responseIsFollowed
          ) {
            // TODO: handle error
            dispatch(createActionArtistErrorDetails());
          } else {
            const topTracks = responseTracks.data;
            const albums = responseAlbums.data;
            const appearsOn = responseApearsOn.data;
            const relatedArtists = responseRelatedArtists.data;
            const isFollowed = responseIsFollowed.data[0];

            const topTrackIds = topTracks.tracks.map((track) => track.id);
            const responseFavTracks =
              topTrackIds.length > 0
                ? await apiMannager.getAllEntities<boolean[]>(
                    'libraryContains',
                    {
                      uris: urisParameter('track', topTrackIds),
                    }
                  )
                : await emptyContainsResponse();
            if ('error' in responseFavTracks) {
              dispatch(createActionArtistErrorDetails());
            } else {
              const topTracksWithFavorite = topTracks.tracks.map(
                (track, index) => ({
                  ...track,
                  favorite: responseFavTracks.data[index],
                })
              );

              dispatch(
                createActionArtistAddDetails({
                  details,
                  topTracks: topTracksWithFavorite,
                  albums,
                  selectedAlbumType: albumTypes[0].id,
                  apearsOn: appearsOn,
                  relatedArtists: relatedArtists.artists,
                  isFollowed: isFollowed,
                })
              );
              dispatch(createActionArtistStopLoading());
            }
          }
        }
      }
    };

    getArtistDetails();
  }, [dispatch, id, protectedSpotify]);

  useEffect(() => {
    const getAlbums = async () => {
      if (selectedAlbumType !== undefined) {
        const apiMannager = new ApiManager(spotifyApiConfig, protectedSpotify);

        const parameters: { limit: number; include_groups?: string } = {
          limit: albumsLimit,
        };

        if (selectedAlbumType !== '') {
          parameters.include_groups = selectedAlbumType;
        }

        const responseAlbums = await apiMannager.getEntity<IArtistAlbums>(
          'artists',
          `${id}/albums`,
          parameters
        );
        if ('error' in responseAlbums) {
          dispatch(createActionArtistErrorDetails());
        } else {
          const albums = responseAlbums.data;
          dispatch(createActionArtistSetAlbums(albums));
        }
      }
    };

    getAlbums();
  }, [dispatch, id, protectedSpotify, selectedAlbumType]);

  useEffect(() => {
    if (details) {
      Vibrant.from(details.images[0].url)
        .getPalette()
        .then((palette) => setDominantColor(palette.Vibrant?.getRgb()));
    }
  }, [details]);

  useEffect(() => {
    return () => {
      dispatch(createActionArtistReset());
    };
  }, [dispatch]);

  const handleClickFollow = async () => {
    const apiMannager = new ApiManager(spotifyApiConfig, protectedSpotify);
    if (isFollowed) {
      const responseUnfollow = await apiMannager.deleteEntity('library', '', {
        uris: urisParameter('artist', id ?? ''),
      });

      if ('error' in responseUnfollow) {
        //TODO: handle error
      } else {
        dispatch(createActionArtistSetIsFollowed(false));
      }
    } else {
      const responseFollow = await apiMannager.replaceEntity(
        'library',
        '',
        undefined,
        {
          uris: urisParameter('artist', id ?? ''),
        }
      );

      if ('error' in responseFollow) {
        //TODO: handle error
      } else {
        //
        dispatch(createActionArtistSetIsFollowed(true));
      }
    }
  };

  const getBannerStyle = () => {
    const style: HTMLAttributes<HTMLDivElement>['style'] = {};
    if (dominantColor) {
      const color = tinyColor(`rgb(${dominantColor.join(', ')})`);
      const darkerDominant = tinyColor(
        `rgb(${dominantColor.join(', ')})`
      ).darken(22);
      style.backgroundImage = `linear-gradient(${color.toRgbString()}, ${darkerDominant.toRgbString()})`;
    }
    return style;
  };

  const getBodyStyle = () => {
    const style: HTMLAttributes<HTMLDivElement>['style'] = {};
    if (dominantColor) {
      const darkerDominant = tinyColor(
        `rgb(${dominantColor.join(', ')})`
      ).darken(28);
      style.backgroundImage = `linear-gradient(${darkerDominant.toRgbString()}, transparent 240px)`;
    }
    return style;
  };

  const handleClickPlay = async () => {
    const apiManager = new ApiManager(spotifyApiConfig, protectedSpotify);

    const response = await apiManager.replaceEntity(
      'playerPlay',
      '',
      {
        context_uri: details?.uri,
      },
      {
        device_id: retrieveDeviceId(),
      }
    );
    if ('error' in response) {
      // TODO: handle error
      // eslint-disable-next-line no-console
      console.log('Cant reproduce');
    }
  };

  return (
    <>
      {_loading || _error ? (
        <LoadingPage />
      ) : (
        <div className="mt-theme-top-bar-negative-height">
          <div className="pt-theme-top-bar-height" style={getBannerStyle()}>
            <div className="flex gap-6 pt-9 p-theme-page-padding">
              <div className=" w-[192px] 2xl:w-[232px]">
                <div className="w-full aspect-square overflow-hidden rounded-full shadow-2xl shadow-theme-shadow">
                  <img
                    className="  h-full w-full object-cover"
                    ref={imageReference}
                    src={details?.images[0].url}
                    alt=""
                  />
                </div>
              </div>
              <div className=" flex-1 flex flex-col justify-end pb-1">
                <Header
                  type="h1"
                  className="text-[6rem] leading-[6rem] mb-9 font-bold tracking-tighter"
                >
                  {details?.name}
                </Header>
                <span>{`${details?.followers.total.toLocaleString()} followers`}</span>
              </div>
            </div>
          </div>
          <div
            className="min-h-full p-theme-page-padding"
            style={getBodyStyle()}
          >
            <div className="flex flex-row gap-8 mb-theme-page-padding">
              <PlayButton onClick={handleClickPlay} />
              <TransparentButton
                className=" self-center"
                onClick={handleClickFollow}
              >
                {isFollowed ? 'Following' : 'Follow'}
              </TransparentButton>
            </div>
            <div className=" mb-10">
              <Header type="h2" className={header2ClassName}>
                Popular
              </Header>
              <div>
                {topTracks?.map((track, index) => {
                  const handleClickFavoriteTrack = async () => {
                    const apiMannager = new ApiManager(
                      spotifyApiConfig,
                      protectedSpotify
                    );
                    if (track.favorite) {
                      const responseRemoveFromFavorites =
                        await apiMannager.deleteEntity('library', '', {
                          uris: urisParameter('track', track.id),
                        });

                      if ('error' in responseRemoveFromFavorites) {
                        //TODO: handle error
                      } else {
                        dispatch(
                          createActionArtistSetTrackIsFavorite({
                            track: track.id,
                            favorite: false,
                          })
                        );
                      }
                    } else {
                      const responseAddToFavorites =
                        await apiMannager.replaceEntity(
                          'library',
                          '',
                          undefined,
                          {
                            uris: urisParameter('track', track.id),
                          }
                        );

                      if ('error' in responseAddToFavorites) {
                        //TODO: handle error
                      } else {
                        //
                        dispatch(
                          createActionArtistSetTrackIsFavorite({
                            track: track.id,
                            favorite: true,
                          })
                        );
                      }
                    }
                  };

                  return expandTopTracks || index <= 4 ? (
                    <TrackListItem
                      id={track.id}
                      onFavorite={handleClickFavoriteTrack}
                      key={track.id}
                      explicit={track.explicit}
                      image={track.album.images[0].url}
                      name={track.name}
                      duration={track.duration_ms}
                      number={index + 1}
                      followed={track.favorite}
                      uri={track.uri}
                      contextUri={track.album.uri}
                    />
                  ) : undefined;
                })}
              </div>
              <span
                className=" cursor-pointer ml-4 text-theme-light-gray hover:text-theme-white"
                onClick={() =>
                  setExpandTopTracks((previousExpand) => !previousExpand)
                }
              >
                {topTracks &&
                  topTracks.length >= 5 &&
                  `See ${expandTopTracks ? 'less' : 'more'}`}
              </span>
            </div>
            <div>
              <div className="flex justify-between items-center">
                <Header type="h2" className={header2ClassName}>
                  Discography
                </Header>
                <Link
                  className="link"
                  to={`${ROUTE_NAMES.ARTIST}/${id}/discography`}
                >
                  Show all
                </Link>
              </div>
              <div className=" flex gap-2 mb-6">
                {albumTypes.map((albumType) => {
                  const handleAlbumTypeClick = () =>
                    dispatch(createActionArtistSetAlbumType(albumType.id));
                  return (
                    <Badge
                      key={albumType.id}
                      selected={selectedAlbumType === albumType.id}
                      onClick={handleAlbumTypeClick}
                      text={albumType.text}
                    />
                  );
                })}
              </div>
              <PreviewLayout>
                {albums?.items.map((album, index) => {
                  if (index >= getItemsPerBreakpoint(viewportWidth)) {
                    return;
                  }

                  const handleOnClick = () =>
                    navigate(`${ROUTE_NAMES.ALBUM}/${album.id}`);

                  return (
                    <TopItemPreview
                      key={album.id}
                      name={album.name}
                      image={album.images[0].url}
                      releaseYear={Number(album.release_date.slice(0, 4))}
                      type={capitalize(album.album_type)}
                      onClick={handleOnClick}
                      uri={album.uri}
                    />
                  );
                })}
              </PreviewLayout>
            </div>
            <div>
              <div className="flex justify-between items-center">
                <Header type="h2" className={header2ClassName}>
                  Fans also like
                </Header>
                <Link
                  className="link"
                  to={`${ROUTE_NAMES.ARTIST}/${id}/related`}
                >
                  Show all
                </Link>
              </div>
              <PreviewLayout>
                {relatedArtists?.map((artist, index) => {
                  if (index >= getItemsPerBreakpoint(viewportWidth)) {
                    return;
                  }
                  const { id, name, images, uri } = artist;
                  const handleOnClick = () =>
                    navigate(`${ROUTE_NAMES.ARTIST}/${id}`);
                  return (
                    <ArtistPreview
                      uri={uri}
                      key={id}
                      onClick={handleOnClick}
                      name={name}
                      image={images[0].url}
                    />
                  );
                })}
              </PreviewLayout>
            </div>
            <div>
              <div className="flex justify-between items-center">
                <Header type="h2" className={header2ClassName}>
                  Appears On
                </Header>
                <Link
                  className="link"
                  to={`${ROUTE_NAMES.ARTIST}/${id}/appears-on`}
                >
                  Show all
                </Link>
              </div>
              <PreviewLayout>
                {apearsOn?.items.map((album, index) => {
                  if (index >= getItemsPerBreakpoint(viewportWidth)) {
                    return;
                  }

                  const handleOnClick = () =>
                    navigate(`${ROUTE_NAMES.ALBUM}/${album.id}`);

                  return (
                    <TopItemPreview
                      key={album.id}
                      name={album.name}
                      image={album.images[0].url}
                      releaseYear={Number(album.release_date.slice(0, 4))}
                      type={capitalize(album.type)}
                      onClick={handleOnClick}
                      uri={album.uri}
                    />
                  );
                })}
              </PreviewLayout>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Artist;
